import React, {useCallback, useEffect, useState} from 'react';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
  Box,
  Chip
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import {makeStyles} from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';
import {useSnackbar} from 'notistack';
import {useSelector, useDispatch} from 'react-redux';
import {transceive} from './actions';
import {parseCBOR, hexStringToByte, byteToHexString} from './util';
import {setPin as ctapSetPin, changePin as ctapChangePin} from './ctap2-pin-protocol';
import {getAllCredentials as ctapGetAllCredentials, deleteCredential as ctapDeleteCredential} from './ctap2-credential-mgmt-protocol';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  grid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(4),
    },
  },
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
    },
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(2),
  },
  credentialItem: {
    marginBottom: theme.spacing(1),
  },
  algorithmChip: {
    margin: theme.spacing(0.5),
  },
  pinStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  retryWarning: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
  retryNormal: {
    color: theme.palette.text.secondary,
  }
}));

export default function FIDO2() {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {enqueueSnackbar} = useSnackbar();
  const dispatch = useDispatch();
  const device = useSelector(state => state.device);
  const adminAuthenticated = useSelector(state => state.adminAuthenticated);
  
  // State
  const [loading, setLoading] = useState(false);
  const [aaguid, setAaguid] = useState('');
  const [algorithms, setAlgorithms] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [pinSet, setPinSet] = useState(false);
  const [pinRetries, setPinRetries] = useState(0);
  const [credentials, setCredentials] = useState([]);
  
  // 🔖 分页相关 state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // 每页显示 10 个凭据
  
  // Dialog states
  const [setPinDialogOpen, setSetPinDialogOpen] = useState(false);
  const [changePinDialogOpen, setChangePinDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [pinAuthDialogOpen, setPinAuthDialogOpen] = useState(false);
  
  // Form states
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [authPin, setAuthPin] = useState('');
  const [pinAuthAction, setPinAuthAction] = useState('');
  const [selectedCred, setSelectedCred] = useState(null);

  // Load FIDO2 info using CTAP2 over APDU
  const loadInfo = useCallback(async () => {
    if (!device) return;
    
    setLoading(true);
    try {
      // 1. Select FIDO2 applet
      // APDU: 00A40400 08 A0000006472F0001
      const selectResponse = await dispatch(transceive('00A4040008A0000006472F0001'));
      
      if (selectResponse.slice(-4) === '9000') {
        // 2. Send CTAP2 GET_INFO command (0x04)
        // APDU: 80 10 00 00 01 04
        // CLA=0x80 (FIDO2), INS=0x10 (CTAP_INS_MSG), Lc=0x01, Data=0x04 (GET_INFO)
        const infoResponse = await dispatch(transceive('801000000104'));
        
        if (infoResponse.slice(-4) === '9000') {
          // 3. Parse CBOR response
          const cborHex = infoResponse.slice(0, -4);
          const cborBytes = hexStringToByte(cborHex);
          
          // Check if response starts with success status (0x00)
          if (cborBytes[0] === 0x00) {
            // Parse CBOR data (skip status byte)
            const info = parseCBOR(cborBytes.slice(1));
            
            // Extract information from CBOR map
            // Note: Firmware version (0x14) is available but shown in Overview page
            
            // AAGUID (0x03)
            const aaguidBytes = info[0x03];
            if (aaguidBytes) {
              const aaguidHex = byteToHexString(aaguidBytes);
              setAaguid(aaguidHex.match(/.{2}/g).join('-'));
            } else {
              setAaguid('Unknown');
            }
            
            // Algorithms (0x09)
            const algs = info[0x09] || [];
            const algNames = algs.map(alg => {
              if (alg.alg === -7) return 'ES256';
              if (alg.alg === -8) return 'EdDSA';
              if (alg.alg === -257) return 'RS256';
              return `ALG${alg.alg}`;
            });
            setAlgorithms(algNames);
            
            // Extensions (0x02)
            setExtensions(info[0x02] || []);
            
            // Options (0x04)
            const options = info[0x04] || {};
            setPinSet(options.clientPin === true);
            
            // Get PIN retries if PIN is set
            if (options.clientPin) {
              try {
                // CTAP2 CLIENT_PIN getRetries (subCommand=0x01)
                // APDU: 80 10 00 00 03 06 A1 01 01
                const retriesResponse = await dispatch(transceive('801000000306A10101'));
                if (retriesResponse.slice(-4) === '9000') {
                  const retriesBytes = hexStringToByte(retriesResponse.slice(0, -4));
                  if (retriesBytes[0] === 0x00) {
                    const retriesInfo = parseCBOR(retriesBytes.slice(1));
                    setPinRetries(retriesInfo[0x03] || 0); // retries
                  }
                }
              } catch (e) {
                console.warn('Failed to get PIN retries:', e);
              }
            } else {
              setPinRetries(0);
            }
            
            enqueueSnackbar('FIDO2 info loaded successfully', {variant: 'success'});
          } else {
            throw new Error(`CTAP error: 0x${cborBytes[0].toString(16)}`);
          }
        } else {
          throw new Error(`APDU error: ${infoResponse.slice(-4)}`);
        }
      } else {
        // Check if error is 6982 (security status not satisfied)
        if (selectResponse.slice(-4) === '6982') {
          console.log('⚠️ FIDO2 applet requires Admin authentication');
          // Admin auth dialog will automatically open, so just return silently
          return;
        }
        throw new Error(`Failed to select FIDO2 applet: ${selectResponse.slice(-4)}`);
      }
    } catch (e) {
      // Silently handle 6982 errors (Admin authentication required)
      if (e.message && e.message.includes('6982')) {
        console.log('⚠️ FIDO2 info loading requires Admin authentication');
        return;
      }
      enqueueSnackbar(`Failed to load FIDO2 info: ${e.message}`, {variant: 'error'});
    } finally {
      setLoading(false);
    }
  }, [device, enqueueSnackbar, dispatch]);

  // Load credentials using CTAP2 Credential Management
  const loadCredentials = useCallback(async (pin) => {
    if (!device || !pinSet) {
      setCredentials([]);
      return;
    }
    
    if (!pin) {
      // Prompt for FIDO2 PIN
      // Note: Currently CTAP2 Credential Management requires FIDO2 PIN
      // TODO: Support using Admin authentication as alternative
      setPinAuthAction('load');
      setPinAuthDialogOpen(true);
      return;
    }
    
    setLoading(true);
    try {
      enqueueSnackbar('Loading credentials...', {variant: 'info'});
      
      // Select CTAP applet first
      console.log('[Load Credentials] Selecting FIDO2 applet...');
      let res = await dispatch(transceive('00A4040008A0000006472F0001'));
      if (!res.endsWith('9000')) {
        throw new Error('Failed to select FIDO2 applet');
      }
      
      // Create transceive wrapper for the protocol functions
      const transceiveFn = async (apdu) => {
        return await dispatch(transceive(apdu));
      };
      
      // Get all credentials
      const creds = await ctapGetAllCredentials(transceiveFn, pin);
      setCredentials(creds);
      setCurrentPage(1); // 🔖 重置到第一页
      
      if (creds.length === 0) {
        enqueueSnackbar('No discoverable credentials found', {variant: 'info'});
      } else {
        enqueueSnackbar(`Found ${creds.length} credential(s)`, {variant: 'success'});
      }
    } catch (e) {
      enqueueSnackbar(`Failed to load credentials: ${e.message}`, {variant: 'error'});
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  }, [device, pinSet, enqueueSnackbar, dispatch]);

  // Set PIN using CTAP2 Client PIN protocol
  const handleSetPin = useCallback(async () => {
    if (newPin.length < 4) {
      enqueueSnackbar('PIN must be at least 4 characters', {variant: 'error'});
      return;
    }
    
    if (newPin.length > 63) {
      enqueueSnackbar('PIN must be at most 63 characters', {variant: 'error'});
      return;
    }
    
    if (newPin !== confirmPin) {
      enqueueSnackbar('PINs do not match', {variant: 'error'});
      return;
    }
    
    // 检查是否与默认密码相同
    if (newPin === '123456') {
      enqueueSnackbar('⚠️ PIN cannot be the default password (123456). Please choose a different PIN.', {variant: 'warning'});
      return;
    }
    
    // 检查密码过于简单
    const weakPatterns = [
      /^(.)\1+$/,           // 全是相同数字，如 111111, 000000
      /^(01)+0?$/,          // 重复模式，如 010101
      /^(12)+1?$/,          // 重复模式，如 121212
      /^123456/,            // 顺序，如 1234567890
      /^987654/,            // 倒序，如 9876543210
      /^(abc)+/i,           // 字母顺序
    ];
    
    if (weakPatterns.some(pattern => pattern.test(newPin))) {
      enqueueSnackbar('⚠️ PIN is too weak. Avoid simple patterns like 111111, 123456, or 121212.', {variant: 'warning'});
      return;
    }
    
    // 检查过短的数字 PIN（虽然已经有长度检查，但这里额外提醒）
    if (/^\d+$/.test(newPin) && newPin.length < 6) {
      enqueueSnackbar('⚠️ For numeric PINs, we recommend at least 6 digits for better security.', {variant: 'info'});
      // 不阻止，只是提醒
    }
    
    setLoading(true);
    try {
      enqueueSnackbar('Setting PIN...', {variant: 'info'});
      
      // Select CTAP applet first
      console.log('[Set PIN] Step 1: Selecting FIDO2 applet...');
      let res = await dispatch(transceive('00A4040008A0000006472F0001'));
      console.log('[Set PIN] Step 1 result:', res);
      if (!res.endsWith('9000')) {
        throw new Error('Failed to select FIDO2 applet');
      }
      
      // Create transceive wrapper
      const transceiveFn = async (apdu) => {
        console.log('[Set PIN] APDU ->', apdu.substring(0, 50) + (apdu.length > 50 ? '...' : ''));
        const response = await dispatch(transceive(apdu));
        console.log('[Set PIN] APDU <-', response);
        return response;
      };
      
      // Set PIN using CTAP2 protocol
      console.log('[Set PIN] Step 2: Calling ctapSetPin...');
      await ctapSetPin(transceiveFn, newPin);
      console.log('[Set PIN] Success!');
      
      enqueueSnackbar('PIN set successfully!', {variant: 'success'});
      setSetPinDialogOpen(false);
      setNewPin('');
      setConfirmPin('');
      
      // Reload info to update PIN status
      await loadInfo();
    } catch (e) {
      enqueueSnackbar(`Failed to set PIN: ${e.message}`, {variant: 'error'});
    } finally {
      setLoading(false);
    }
  }, [newPin, confirmPin, enqueueSnackbar, dispatch, loadInfo]);

  // Change PIN using CTAP2 Client PIN protocol
  const handleChangePin = useCallback(async () => {
    if (currentPin.length < 4 || newPin.length < 4) {
      enqueueSnackbar('PIN must be at least 4 characters', {variant: 'error'});
      return;
    }
    
    if (newPin !== confirmPin) {
      enqueueSnackbar('New PINs do not match', {variant: 'error'});
      return;
    }
    
    // 检查是否与默认密码相同
    if (newPin === '123456') {
      enqueueSnackbar('⚠️ PIN cannot be the default password (123456). Please choose a different PIN.', {variant: 'warning'});
      return;
    }
    
    // 检查密码过于简单
    const weakPatterns = [
      /^(.)\1+$/,           // 全是相同数字，如 111111, 000000
      /^(01)+0?$/,          // 重复模式，如 010101
      /^(12)+1?$/,          // 重复模式，如 121212
      /^123456/,            // 顺序，如 1234567890
      /^987654/,            // 倒序，如 9876543210
      /^(abc)+/i,           // 字母顺序
    ];
    
    if (weakPatterns.some(pattern => pattern.test(newPin))) {
      enqueueSnackbar('⚠️ PIN is too weak. Avoid simple patterns like 111111, 123456, or 121212.', {variant: 'warning'});
      return;
    }
    
    // 检查过短的数字 PIN
    if (/^\d+$/.test(newPin) && newPin.length < 6) {
      enqueueSnackbar('⚠️ For numeric PINs, we recommend at least 6 digits for better security.', {variant: 'info'});
      // 不阻止，只是提醒
    }
    
    setLoading(true);
    try {
      enqueueSnackbar('Changing PIN...', {variant: 'info'});
      
      // Select CTAP applet first
      let res = await dispatch(transceive('00A4040008A0000006472F0001'));
      if (!res.endsWith('9000')) {
        throw new Error('Failed to select FIDO2 applet');
      }
      
      // Create transceive wrapper
      const transceiveFn = async (apdu) => {
        return await dispatch(transceive(apdu));
      };
      
      // Change PIN using CTAP2 protocol
      await ctapChangePin(transceiveFn, currentPin, newPin);
      
      enqueueSnackbar('PIN changed successfully!', {variant: 'success'});
      setChangePinDialogOpen(false);
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
      
      // Reload info to update PIN retries
      await loadInfo();
    } catch (e) {
      enqueueSnackbar(`Failed to change PIN: ${e.message}`, {variant: 'error'});
    } finally {
      setLoading(false);
    }
  }, [currentPin, newPin, confirmPin, enqueueSnackbar, dispatch, loadInfo]);

  // Reset FIDO2 using CTAP2 RESET command
  const handleReset = useCallback(async () => {
    try {
      // CTAP2 RESET command (0x07)
      // APDU: 80 10 00 00 01 07
      // Note: Must be called within 10 seconds of power-up
      const resetResponse = await dispatch(transceive('801000000107'));
      
      if (resetResponse.slice(-4) === '9000') {
        const cborBytes = hexStringToByte(resetResponse.slice(0, -4));
        
        // Check status byte
        if (cborBytes[0] === 0x00) {
          enqueueSnackbar('FIDO2 reset successful! All credentials and PIN have been deleted.', {variant: 'success'});
          setPinSet(false);
          setCredentials([]);
          await loadInfo();
        } else {
          throw new Error(`CTAP error: 0x${cborBytes[0].toString(16)}`);
        }
      } else {
        throw new Error(`APDU error: ${resetResponse.slice(-4)}`);
      }
      
      setResetDialogOpen(false);
    } catch (e) {
      enqueueSnackbar(`Failed to reset FIDO2: ${e.message}. Note: Reset must be done within 10 minutes of plugging in the device (re-plug if needed).`, {variant: 'error'});
    }
  }, [enqueueSnackbar, dispatch, loadInfo]);

  // Delete credential using CTAP2 Credential Management
  const handleDeleteCredential = useCallback(async (pin) => {
    if (!selectedCred || !pin) return;
    
    setLoading(true);
    try {
      enqueueSnackbar('Deleting credential...', {variant: 'info'});
      
      // Select CTAP applet first
      console.log('[Delete Credential] Selecting FIDO2 applet...');
      let res = await dispatch(transceive('00A4040008A0000006472F0001'));
      if (!res.endsWith('9000')) {
        throw new Error('Failed to select FIDO2 applet');
      }
      
      // Create transceive wrapper
      const transceiveFn = async (apdu) => {
        return await dispatch(transceive(apdu));
      };
      
      // Delete credential using CTAP2 protocol
      await ctapDeleteCredential(transceiveFn, pin, selectedCred.credentialID);
      
      enqueueSnackbar('Credential deleted successfully!', {variant: 'success'});
      setSelectedCred(null);
      
      // Reload credentials
      await loadCredentials(pin);
    } catch (e) {
      enqueueSnackbar(`Failed to delete credential: ${e.message}`, {variant: 'error'});
    } finally {
      setLoading(false);
    }
  }, [selectedCred, enqueueSnackbar, dispatch, loadCredentials]);

  // Handle PIN authentication for credential operations
  const handlePinAuth = useCallback(async () => {
    if (!authPin || authPin.length < 4) {
      enqueueSnackbar('Please enter your PIN', {variant: 'error'});
      return;
    }
    
    try {
      if (pinAuthAction === 'load') {
        await loadCredentials(authPin);
      } else if (pinAuthAction === 'delete') {
        await handleDeleteCredential(authPin);
      }
      setPinAuthDialogOpen(false);
      setAuthPin('');
    } catch (e) {
      enqueueSnackbar(`Authentication failed: ${e.message}`, {variant: 'error'});
    }
  }, [authPin, pinAuthAction, loadCredentials, handleDeleteCredential, enqueueSnackbar]);

  // Load info on mount (only after Admin authentication)
  useEffect(() => {
    if (device && adminAuthenticated) {
      console.log('✓ Admin authenticated, loading FIDO2 info...');
      (async () => {
        await loadInfo();
        // Note: Don't call verifyAdminAuth here as it may cause unnecessary re-authentication
        // The firmware's 30-minute cache will keep the Admin authentication valid
        // App.js already has periodic verification every 5 minutes
      })();
    } else if (device && !adminAuthenticated) {
      console.log('⚠️ Waiting for Admin authentication before loading FIDO2 info...');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, adminAuthenticated]); // 依赖 device 和 adminAuthenticated

  // Removed auto-load credentials to avoid multiple PIN prompts
  // Users can manually click "Load Credentials" button instead

  if (!device) {
    return (
      <Container className={classes.container}>
        <Typography variant="h5" gutterBottom>
          FIDO2 / WebAuthn Management
        </Typography>
        <Typography color="textSecondary">
          Please connect your CanoKey device to use FIDO2 features.
        </Typography>
      </Container>
    );
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={1} justifyContent={'center'} className={classes.grid}>
        <Grid item xs={10}>
          <Card>
            <CardContent>
              <Typography variant="h3">FIDO2 Applet</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                FIDO2/WebAuthn authentication for passwordless login. Manage your FIDO2 credentials, PINs, and view authenticator information.
              </Typography>
              <Box mt={2} p={2} bgcolor="success.light" borderRadius={4}>
                <Typography variant="body2">
                  <strong>✅ Full CTAP2 Protocol:</strong> PIN management and credential management fully implemented with ECDH encryption and HMAC authentication.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={10}>
          <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            FIDO2 Authenticator Information
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            FIDO2/WebAuthn specific information. For general device info (firmware version, model, serial number), 
            see <strong>Overview</strong> page.
          </Typography>
          <Divider style={{marginBottom: 16}} />
          
          {loading ? (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={20} />
              <Typography>Loading...</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  AAGUID (Authenticator Attestation GUID)
                </Typography>
                <Typography variant="body1" style={{fontFamily: 'monospace', fontSize: '0.9em'}}>
                  {aaguid || 'Unknown'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Unique identifier for this authenticator model
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Supported Algorithms
                </Typography>
                <Box>
                  {algorithms.length > 0 ? (
                    algorithms.map((alg, idx) => (
                      <Chip
                        key={idx}
                        label={alg}
                        size="small"
                        color="primary"
                        variant="outlined"
                        className={classes.algorithmChip}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No algorithms detected
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  CTAP2 Extensions
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                  Additional features supported by this authenticator
                </Typography>
                <Box>
                  {extensions.length > 0 ? (
                    extensions.map((ext, idx) => (
                      <Chip
                        key={idx}
                        label={ext}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        className={classes.algorithmChip}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No extensions detected
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
        </Grid>

        <Grid item xs={10}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            PIN Management
          </Typography>
          <Divider style={{marginBottom: 16}} />
          
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                PIN Status
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5}>
                {pinSet ? (
                  <Chip label="PIN is set" color="primary" size="small" />
                ) : (
                  <Chip label="No PIN set" color="default" size="small" />
                )}
                {pinRetries > 0 && (
                  <Typography 
                    variant="body2" 
                    className={pinRetries <= 3 ? classes.retryWarning : classes.retryNormal}
                  >
                    ({pinRetries} retries left)
                  </Typography>
                )}
              </Box>
            </Box>
            
            <Box mt={2}>
              {!pinSet ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setSetPinDialogOpen(true)}
                >
                  Set PIN
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setChangePinDialogOpen(true)}
                    style={{marginRight: 8}}
                  >
                    Change PIN
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      enqueueSnackbar('PIN verification coming soon', {variant: 'info'});
                    }}
                  >
                    Verify PIN
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
        </Grid>

        <Grid item xs={10}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Discoverable Credentials
          </Typography>
          <Divider style={{marginBottom: 16}} />
          
          {!pinSet ? (
            <Typography color="textSecondary">
              Set a FIDO2 PIN to manage discoverable credentials.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" paragraph>
                View and manage your discoverable credentials (also known as "passkeys" or "resident keys").
                This requires your FIDO2 PIN.
              </Typography>
              
              <Box mb={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    // Always prompt for FIDO2 PIN (avoid auto-loading to reduce PIN prompts)
                    loadCredentials();
                  }}
                  disabled={loading}
                >
                  {credentials.length > 0 ? 'Refresh Credentials' : 'Load Credentials'}
                </Button>
              </Box>
              
              {credentials.length === 0 ? (
                <Typography color="textSecondary">
                  Click "Load Credentials" and enter your FIDO2 PIN to view credentials.
                </Typography>
              ) : (
                <>
                  {/* 🔖 统计信息 */}
                  <Box mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Total: {credentials.length} credential(s)
                      {credentials.length > itemsPerPage && ` (Page ${currentPage} of ${Math.ceil(credentials.length / itemsPerPage)})`}
                    </Typography>
                  </Box>
                  
                  {/* 🔖 凭据列表（当前页） */}
                  <List>
                    {credentials
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map((cred, idx) => {
                        const actualIdx = (currentPage - 1) * itemsPerPage + idx; // 实际索引
                        const rpName = cred.rp?.name || cred.rp?.id || 'Unknown RP';
                        const userName = cred.user?.name || cred.user?.displayName || 'Unknown User';
                        const credId = cred.credentialID?.id ? 
                          Array.from(cred.credentialID.id.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join('') + '...' :
                          'N/A';
                        
                        return (
                          <ListItem key={actualIdx} className={classes.credentialItem}>
                            <ListItemText
                              primary={`${rpName} - ${userName}`}
                              secondary={`Credential ID: ${credId}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => {
                                  setSelectedCred(cred);
                                  setPinAuthAction('delete');
                                  setPinAuthDialogOpen(true);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                  </List>
                  
                  {/* 🔖 分页控件 */}
                  {credentials.length > itemsPerPage && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Pagination
                        count={Math.ceil(credentials.length / itemsPerPage)}
                        page={currentPage}
                        onChange={(event, page) => setCurrentPage(page)}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
        </Grid>

        <Grid item xs={10}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            FIDO2 Reset
          </Typography>
          <Divider style={{marginBottom: 16}} />
          
          <Typography color="textSecondary" paragraph>
            Reset the FIDO2 applet. This will delete all credentials and PIN.
          </Typography>
          
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setResetDialogOpen(true)}
            disabled={loading}
          >
            Reset FIDO2
          </Button>
        </CardContent>
      </Card>
        </Grid>
      </Grid>

      {/* Set PIN Dialog */}
      <Dialog open={setPinDialogOpen} onClose={() => setSetPinDialogOpen(false)} fullScreen={fullScreen}>
        <DialogTitle>Set PIN</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Set a PIN for your FIDO2 authenticator. The PIN must be 4-63 characters long.
          </DialogContentText>
          <form onSubmit={(e) => { e.preventDefault(); handleSetPin(); }}>
            {/* Hidden username field for accessibility */}
            <input type="text" name="username" autoComplete="username" style={{display: 'none'}} readOnly value="fido2" />
            <TextField
              autoFocus
              margin="dense"
              label="New PIN"
              type="password"
              fullWidth
              value={newPin}
              onChange={(e) => setNewPin(e.target.value)}
              autoComplete="new-password"
            />
            <TextField
              margin="dense"
              label="Confirm PIN"
              type="password"
              fullWidth
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              autoComplete="new-password"
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetPinDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSetPin} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Set PIN'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change PIN Dialog */}
      <Dialog open={changePinDialogOpen} onClose={() => setChangePinDialogOpen(false)} fullScreen={fullScreen}>
        <DialogTitle>Change PIN</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Change your FIDO2 PIN. Enter your current PIN and new PIN.
          </DialogContentText>
          <form onSubmit={(e) => { e.preventDefault(); handleChangePin(); }}>
            {/* Hidden username field for accessibility */}
            <input type="text" name="username" autoComplete="username" style={{display: 'none'}} readOnly value="fido2" />
            <TextField
              autoFocus
              margin="dense"
              label="Current PIN"
              type="password"
              fullWidth
              value={currentPin}
              onChange={(e) => setCurrentPin(e.target.value)}
              autoComplete="current-password"
            />
            <TextField
              margin="dense"
              label="New PIN"
              type="password"
              fullWidth
              value={newPin}
              onChange={(e) => setNewPin(e.target.value)}
              autoComplete="new-password"
            />
            <TextField
              margin="dense"
              label="Confirm New PIN"
              type="password"
              fullWidth
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              autoComplete="new-password"
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePinDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleChangePin} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Change PIN'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} fullScreen={fullScreen}>
        <DialogTitle>Reset FIDO2</DialogTitle>
        <DialogContent>
          <DialogContentText color="error" paragraph>
            ⚠️ WARNING: This will delete ALL credentials and PIN permanently!
          </DialogContentText>
          <DialogContentText paragraph>
            Are you sure you want to reset the FIDO2 applet?
          </DialogContentText>
          <DialogContentText color="textSecondary">
            <strong>Important:</strong> Reset must be done within 10 minutes of plugging in the device.
            If this fails, please re-plug your device and try again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReset} color="secondary" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* PIN Authentication Dialog */}
      <Dialog open={pinAuthDialogOpen} onClose={() => setPinAuthDialogOpen(false)} fullScreen={fullScreen}>
        <DialogTitle>
          {pinAuthAction === 'load' ? 'Load Credentials' : 'Delete Credential'}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={(e) => { e.preventDefault(); handlePinAuth(); }}>
            {/* Hidden username field for accessibility */}
            <input type="text" name="username" autoComplete="username" style={{display: 'none'}} readOnly value="fido2" />
            <TextField
              autoFocus
              margin="dense"
              label="FIDO2 PIN"
              type="password"
              fullWidth
              value={authPin}
              onChange={(e) => setAuthPin(e.target.value)}
              autoComplete="current-password"
              helperText={pinAuthAction === 'load' ? 'Enter your PIN to view stored credentials' : 'Enter your PIN to delete this credential'}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPinAuthDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePinAuth} color="primary" variant="contained">
            {pinAuthAction === 'load' ? 'Load' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}